import { Box, Grid, IconButton } from '@material-ui/core';
import deleteIcon from 'assets/icon/minus.svg';
import restoreIcon from 'assets/icon/restore.svg';
import Button from 'components/Button';
import CloseIconButton from 'components/CloseIconButton';
import { useContextLayout } from 'components/Layout/context';
import { TitleBar } from 'components/Modals/styled-components';
import { useContextSnackbar } from 'components/Snackbar/context';
import { BaseDataList } from 'constants/base-data-list';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';
import { AvatarImageType, getAvatarImage } from 'utils/getAvatarImage';
import HttpClient from 'utils/httpClient';
import { BaseDataHeaderListItem } from '..';
import Dropzone from './Dropzone';

const AvatarWrapper = styled.div`
  position: relative;

  & img {
    max-width: 100%;
    max-height: 100%;
  }

  & .delete-button {
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 100;
  }
`;

interface Props {
  entityId: string;
  show: boolean;
  onClose: () => void;
  profilePictureUrl?: string | null;
  currentEntity: BaseDataHeaderListItem;
}

const UploadModal = ({ show, onClose, entityId, profilePictureUrl, currentEntity }: Props) => {
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const [file, setFile] = useState<File>();
  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [deleted, setDeleted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { baseDataListSelected } = useContextLayout();

  const preview = useMemo(() => {
    if (filePreview) {
      return filePreview;
    } else if (!deleted && profilePictureUrl) {
      return profilePictureUrl;
    } else {
      return undefined;
    }
  }, [profilePictureUrl, filePreview, deleted]);

  useEffect(() => {
    if (file) {
      setDeleted(false);
    }
  }, [file]);

  const handleDelete = () => {
    if (file) {
      setFile(undefined);
    } else {
      setDeleted(!deleted);
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    if (baseDataListSelected === BaseDataList.RESIDENTS) {
      formData.append('residentId', entityId);
    } else if (baseDataListSelected === BaseDataList.EMPLOYEES) {
      formData.append('employeeId', entityId);
    }
    formData.append('file', file ?? '');

    try {
      setUploading(true);
      await HttpClient.post('profile-picture/upload', formData, true);
    } catch (error) {
      showApiError(error);
    } finally {
      setUploading(false);
      onClose();
    }
  };

  return (
    <Modal show={show} backdrop="static" size="lg">
      <Modal.Body>
        <TitleBar>
          <h1>{t('profile_picture_modal_title')}</h1>
          <CloseIconButton onClick={onClose} />
        </TitleBar>
        <Grid container marginTop={3} spacing={2}>
          <Grid item xs={4}>
            <AvatarWrapper>
              {preview ? (
                <img src={preview} onLoad={() => filePreview && URL.revokeObjectURL(filePreview)} />
              ) : (
                <SVG
                  src={getAvatarImage(
                    currentEntity.name.gender,
                    baseDataListSelected === BaseDataList.RESIDENTS
                      ? AvatarImageType.OLD
                      : AvatarImageType.DEFAULT,
                  )}
                />
              )}
              {(file || profilePictureUrl) && (
                <IconButton
                  className="delete-button"
                  disabled={uploading}
                  onClick={() => handleDelete()}
                >
                  <SVG src={!file && deleted ? restoreIcon : deleteIcon} />
                </IconButton>
              )}
            </AvatarWrapper>
          </Grid>
          <Grid item xs={8}>
            <Dropzone setFile={setFile} setErrorMessages={setErrorMessages} disabled={uploading} />
          </Grid>
        </Grid>
        {errorMessages && errorMessages.map((error) => <p style={{ color: 'red' }}>{error}</p>)}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '26px' }}>
          <Button
            disabled={(!file && !(profilePictureUrl && deleted)) || uploading}
            onClick={() => handleSubmit()}
          >
            {t('add')}
          </Button>
        </Box>
      </Modal.Body>
    </Modal>
  );
};

export default UploadModal;
